/**
 * worldUnlock.ts
 * Pure helpers that decide which worlds and secret stages are accessible
 * from the hub world map.
 *
 * Lives next to `worldProgress.ts` (and not in `app/`) because AGENTS.md
 * requires `app/` files to be thin Expo Router wrappers with no
 * gameplay/data logic.
 *
 * @module worldUnlock
 */

import type { SaveData } from '../types/save';
import { SHARDS_PER_WORLD, countCollectedShards } from './worldProgress';

/**
 * Save key used to flag World 1 as cleared.
 *
 * DECISION: World 1's clear signal is `levels['1-4'].completed === true`,
 * not `worlds[1].completed`. The world-progress flag in `SaveData` is
 * declared but is never written by any production code today
 * (`saveManager.createDefaultSave` initialises it to `false` and nothing
 * sets it to `true`). Deriving from the level entry keeps a single source
 * of truth and avoids a save-schema bump until a real world-clear ritual
 * exists (e.g. defeated World 2's boss flips both flags atomically).
 */
const WORLD_CLEAR_LEVEL_ID: Record<number, string> = {
  // World 1 clears when the player finishes the final main level (1-4).
  1: '1-4',
  // World 2 clears when the VoidMothership boss (2-B) is defeated. Hard
  // pinned here so the helper is ready the moment the boss lands; until
  // then this key is never written and the gate returns false correctly.
  2: '2-B',
  // World 3 clears when `levels['3-B'].completed` is true. That flag is
  // written by `persistWorld3Completion('3-B')` — called from the boss-
  // clear handler in `platformerSessionBundle.ts` once the KingRivet
  // arena finishes. The same call also fires `persistWallSlideUnlock`,
  // so the ability bit is in the save by the time this gate reads back.
  3: '3-B',
  // World 4 clears when `levels['4-B'].completed` is true. Mirrors the
  // W3 wiring: `persistWorld4Completion('4-B')` flips this flag and
  // also fires `persistDashUnlock()` so the air-dash ability bit is in
  // the save by the time the world map reads back. World 5's card routes
  // to '/game' and its stages (5-1..5-4 → 5-B) are all registered, so
  // this gate is the only thing standing between a 4-B clear and the
  // Eclipse Bastion.
  4: '4-B',
};

/** Optional dev overrides surfaced from the Settings screen. */
export interface UnlockOverrides {
  /** Mirrors the `devUnlockWorld2` user setting. */
  readonly devUnlockWorld2: boolean;
  /** Mirrors the `devUnlockWorld3` user setting. */
  readonly devUnlockWorld3: boolean;
  /** Mirrors the `devUnlockWorld4` user setting. */
  readonly devUnlockWorld4: boolean;
}

/**
 * Decide whether `worldId` is selectable on the hub world map.
 *
 * Rules:
 * - World 1 is always unlocked.
 * - World 2 is unlocked when `levels['1-4'].completed === true`, or when
 *   the `devUnlockWorld2` setting is on.
 * - World 3 is unlocked when `levels['2-B'].completed === true`, or when
 *   the `devUnlockWorld3` setting is on. (Hardened for the moment that
 *   boss ships; until then only the dev toggle surfaces the card.)
 * - World 4 is unlocked when `levels['3-B'].completed === true`, or when
 *   the `devUnlockWorld4` setting is on.
 * - World 5 is unlocked when `levels['4-B'].completed === true`. There is
 *   no `devUnlockWorld5` override: World 5 is the last world and its gate
 *   is reachable in normal play, so the dev toggles stop at World 4.
 *
 * @param save      - SaveData to read, or null for a missing/corrupt save.
 * @param worldId   - World id (1..5).
 * @param overrides - Dev unlock toggles forwarded from the Settings screen.
 * @returns `true` when the world card is tappable.
 */
export function isWorldUnlocked(
  save: SaveData | null,
  worldId: number,
  overrides: UnlockOverrides,
): boolean {
  if (worldId === 1) return true;
  if (worldId === 2 && overrides.devUnlockWorld2) return true;
  if (worldId === 3 && overrides.devUnlockWorld3) return true;
  if (worldId === 4 && overrides.devUnlockWorld4) return true;
  const previousClearLevel = WORLD_CLEAR_LEVEL_ID[worldId - 1];
  if (previousClearLevel == null) return false;
  return save?.levels[previousClearLevel]?.completed === true;
}

/**
 * Decide whether the secret stage for `worldId` should be visible on the
 * hub world map. Hidden until the player has collected every main-level
 * Constellation Shard for that world (12 / 12 by ROADMAP definition).
 *
 * DECISION: This intentionally returns `false` for worlds that do not yet
 * ship a shard system — `countCollectedShards` only counts main-level
 * entries (`X-1`..`X-4`) so worlds whose main levels are still stubs will
 * always read 0 and the secret stage stays correctly hidden.
 *
 * @param save    - SaveData to read, or null for a missing/corrupt save.
 * @param worldId - World id (1..5).
 * @returns `true` when the secret stage button should render.
 */
export function isSecretStageUnlocked(save: SaveData | null, worldId: number): boolean {
  return countCollectedShards(save, worldId) >= SHARDS_PER_WORLD;
}
